import React, { useState } from 'react'
import { Button, Form, Grid, GridRow, GridColumn, Icon } from 'semantic-ui-react'
import toast from 'react-hot-toast'

import './index.scss'

import type { AccordionItem } from './index.d'

interface EditAccordionsComponentProps {
    accordions?: AccordionItem[]
    linkedin_url?: string
    researchgate_url?: string
    services: {
        employeeManager: string
    }
    profileLinks: {
        norwegian: string
        english: string
    }
    displayName: string
    accordionTitles: Array<{
        norwegian: string
        english: string
    }>
}

export default function EditAccordionsComponent(props: EditAccordionsComponentProps) {
    // Initialize with 5 static items if not provided
    const initializeAccordions = () => {
        if (props.accordions && props.accordions.length === 5) {
            // Use existing accordions but ensure they have the static titles
            return props.accordions.map((item, index) => ({
                norwegian: {
                    title: props.accordionTitles[index].norwegian,
                    text: item.norwegian?.text || ''
                },
                english: {
                    title: props.accordionTitles[index].english,
                    text: item.english?.text || ''
                }
            }))
        }

        // Create 5 new items with static titles
        return props.accordionTitles.map(titles => ({
            norwegian: { title: titles.norwegian, text: '' },
            english: { title: titles.english, text: '' }
        }))
    }

    const [accordions, setAccordions] = useState<AccordionItem[]>(initializeAccordions())
    const [linkedinUrl, setLinkedinUrl] = useState(props.linkedin_url || '')
    const [researchgateUrl, setResearchgateUrl] = useState(props.researchgate_url || '')

    const handleNorwegianTextChange = (index: number, value: string) => {
        const newAccordions = [...accordions]
        newAccordions[index] = {
            ...newAccordions[index],
            norwegian: {
                ...newAccordions[index].norwegian,
                title: props.accordionTitles[index].norwegian,
                text: value
            }
        }
        setAccordions(newAccordions)
    }

    const handleEnglishTextChange = (index: number, value: string) => {
        const newAccordions = [...accordions]
        newAccordions[index] = {
            ...newAccordions[index],
            english: {
                ...newAccordions[index].english,
                title: props.accordionTitles[index].english,
                text: value
            }
        }
        setAccordions(newAccordions)
    }

    const [loading, setLoading] = useState(false)
    const handleSave = async () => {
        setLoading(true)
        try {
            const response = await fetch(props.services.employeeManager, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    accordions,
                    linkedin_url: linkedinUrl || undefined,
                    researchgate_url: researchgateUrl || undefined
                })
            })

            if (!response.ok) {
                throw new Error('Failed to save settings')
            }

            toast.success('Settings saved successfully')
        } catch {
            toast.error('Error saving settings');
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="edit-accordions">
            <div className="edit-accordions__profile-links" style={{ marginBottom: '2.5rem' }}>
                <h2 className="ui header" style={{ textAlign: 'left' }}>Social profile links</h2>
                <Form style={{ marginBottom: '2rem' }}>
                    <Form.Field>
                        <label>LinkedIn URL</label>
                        <input
                            type="url"
                            value={linkedinUrl}
                            onChange={(e) => setLinkedinUrl(e.target.value)}
                            placeholder="https://linkedin.com/in/..."
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>ResearchGate URL</label>
                        <input
                            type="url"
                            value={researchgateUrl}
                            onChange={(e) => setResearchgateUrl(e.target.value)}
                            placeholder="https://researchgate.net/profile/..."
                        />
                    </Form.Field>
                </Form>
                {props.profileLinks && (
                    <>
                        <h3 className="ui header" style={{ textAlign: 'left', marginTop: '1rem' }}>Your employee pages</h3>
                        <ul>
                            <li>
                                Norwegian page: {' '}
                                <a
                                    href={props.profileLinks.norwegian}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {props.displayName}
                                </a>
                            </li>
                            <li>
                                English page: {' '}
                                <a
                                    href={props.profileLinks.english}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {props.displayName}
                                </a>
                            </li>
                        </ul>
                    </>
                )}
            </div>
            <div className="edit-accordions__header">
                <h2 className="ui header" style={{ textAlign: 'left' }}>Accordions</h2>
                <div className="edit-accordions__actions">
                    <Button
                        primary
                        loading={loading}
                        onClick={() => handleSave()}
                    >
                        <Icon name="save" />
                        Save
                    </Button>
                </div>
            </div>

            <Form>
                {accordions.map((item, index) => (
                    <div key={index} className="edit-accordions__row">
                        <div className="edit-accordions__row-card">
                            <div className="edit-accordions__row-header">
                                <h3>{props.accordionTitles[index].norwegian} / {props.accordionTitles[index].english}</h3>
                            </div>
                            <Grid columns={2}>
                                <GridRow>
                                    <GridColumn>
                                        <Form.Field>
                                            <label>Norwegian Text</label>
                                            <textarea
                                                value={item.norwegian?.text || ''}
                                                onChange={(e) => handleNorwegianTextChange(index, e.target.value)}
                                                placeholder="Enter Norwegian text"
                                                rows={4}
                                            />
                                        </Form.Field>
                                    </GridColumn>
                                    <GridColumn>
                                        <Form.Field>
                                            <label>English Text</label>
                                            <textarea
                                                value={item.english?.text || ''}
                                                onChange={(e) => handleEnglishTextChange(index, e.target.value)}
                                                placeholder="Enter English text"
                                                rows={4}
                                            />
                                        </Form.Field>
                                    </GridColumn>
                                </GridRow>
                            </Grid>
                        </div>
                    </div>
                ))}
            </Form>
        </div>
    )
}